import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import apiClient from "../../../../services/apiClient";
import { setSection, updateSection } from "../../assignmentSlice";
import { BOOK_SCREEN_HEIGHT } from "./EbookPage";
import PageFieldLayer from "./PageFieldLayer";
import { TILE_GAP, buildCropList, buildScreenList, tileWidthOf } from "./paperCrop";
import "./EbookPage.css";
import "./PaperCropPage.css";

// 원서 문제지(좌표가 실린 원서 세트, api/assignment/paper_pages.py)를 화면 크기만큼 잘라 푼다(2026-10-18).
// 쪽을 통째로 넘기면 폰에서는 글이 작고 PC 가로 화면에서는 위아래가 넘친다. 그래서 쪽 그림을 문항이
// 갈리지 않는 자리에서 잘라(paperCrop.js) 폰은 세로로 길게, PC 는 가로로 넓게 한 조각씩 보여 준다.
// 넓은 가로 화면에서 두 단 쪽의 한 단처럼 좁은 조각은 둘을 나란히 놓는다.
//
// 쪽 위에 바로 쓰고 채점하는 것은 리더스북(EbookPage)과 같다. 다만 화면에 보이는 칸만 매긴다
// (field_ids). 저장 모양도 같고 매긴 칸을 graded_fields 로 더 적는다. 다음에 들어오면 보던 조각부터 연다.

const STATE_TYPE = "ebook_state";
const ACCENT = "#D14D72";
const SWIPE_DISTANCE = 50;
const RESPONSE_SAVE_DELAY = 1500;
const CIRCLED_NUMBER = "①②③④⑤⑥⑦⑧⑨⑩⑪⑫⑬⑭⑮⑯⑰⑱⑲⑳";
const MINIMUM_FIT_SHARE = 0.6;

const isNarrowScreen = () =>
  typeof window !== "undefined" && Math.min(window.innerWidth, window.screen.width) <= 768;

const readSavedState = (answerList) => {
  const saved = (answerList || []).find((one) => one && one.type === STATE_TYPE) || {};
  return {
    lastPage: Number.isInteger(saved.last_page) ? saved.last_page : null,
    lastTop: typeof saved.last_top === "number" ? saved.last_top : 0,
    viewedPages: saved.viewed_pages || [],
    responses: saved.responses || {},
    results: saved.results || {},
    gradedPages: saved.graded_pages || [],
    gradedFields: saved.graded_fields || [],
    speaking: saved.speaking || {},
  };
};

// 조각의 점수. 선 잇기는 쌍마다 하나로 센다(EbookPage 와 같다).
const scoreTextOf = (fieldList, results) => {
  let right = 0;
  let total = 0;
  fieldList.forEach((field) => {
    const result = results[field.id] || {};
    if (field.type === "match" && result.total_count) {
      right += result.right_count || 0;
      total += result.total_count;
    } else {
      right += result.score ?? 0;
      total += 1;
    }
  });
  return `${Number.isInteger(right) ? right : right.toFixed(1)} / ${total}`;
};

const PaperCropPage = ({ setCurrentStep, isPreview, isReview }) => {
  const dispatch = useDispatch();
  const { section } = useSelector((state) => state.assignment);
  const question = section?.question || {};
  const book = question.book || {};
  const accent = book.theme_color || ACCENT;

  const savedState = useMemo(() => readSavedState(section?.answer), [section?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [pageList, setPageList] = useState(() => question.pages || []);
  const [isMobile, setIsMobile] = useState(isNarrowScreen);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const [failedPages, setFailedPages] = useState([]);
  const [responses, setResponses] = useState(savedState.responses);
  const [results, setResults] = useState(savedState.results);
  const [gradedPages] = useState(savedState.gradedPages);
  const [gradedFields, setGradedFields] = useState(savedState.gradedFields);
  const [isGrading, setIsGrading] = useState(false);
  const [isMemoOpen, setIsMemoOpen] = useState(false);
  // 지금 보는 곳을 '쪽 + 조각 윗자리' 로 기억한다. 화면 크기가 바뀌어 조각이 달라져도 같은 자리에 머문다.
  const [anchor, setAnchor] = useState(() => ({
    page: savedState.lastPage ?? (question.pages || [])[0]?.page ?? 1,
    top: savedState.lastTop,
  }));

  // section 이 아니라 section.id 에 건다. 저장할 때마다 updateSection 이 state.section 을 갈아끼운다.
  useEffect(() => {
    setPageList(section?.question?.pages || []);
  }, [section?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const stageRef = useRef(null);
  useLayoutEffect(() => {
    if (!stageRef.current) return undefined;
    const measure = () => {
      const rectangle = stageRef.current.getBoundingClientRect();
      setStageSize((previous) =>
        Math.abs(previous.width - rectangle.width) < 2 && Math.abs(previous.height - rectangle.height) < 2
          ? previous
          : { width: rectangle.width, height: rectangle.height }
      );
    };
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(stageRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(isNarrowScreen());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const cropList = useMemo(
    () => (stageSize.width > 0 ? buildCropList(pageList, stageSize.width, stageSize.height) : []),
    [pageList, stageSize]
  );
  const screenList = useMemo(
    () => buildScreenList(cropList, stageSize.width, stageSize.height),
    [cropList, stageSize]
  );

  // 그 쪽에서 윗자리가 기억한 자리보다 위인 조각 가운데 마지막 것. 조각은 칸을 다 보이려 조금 겹칠 수 있다.
  const screenIndexOfAnchor = () => {
    let cropIndex = -1;
    cropList.forEach((crop, index) => {
      if (crop.page === anchor.page && crop.top <= anchor.top + 0.002) cropIndex = index;
    });
    const fallbackIndex = cropList.findIndex((crop) => crop.page >= anchor.page);
    const target = cropIndex >= 0 ? cropIndex : Math.max(0, fallbackIndex);
    return Math.max(0, screenList.findIndex((screen) => screen.includes(target)));
  };
  const currentScreenIndex = screenIndexOfAnchor();
  const currentCropList = (screenList[currentScreenIndex] || []).map((index) => cropList[index]).filter(Boolean);
  const isLastScreen = currentScreenIndex >= screenList.length - 1;

  const gradedFieldSet = useMemo(() => {
    const set = new Set(gradedFields);
    pageList.forEach((page) => {
      if (gradedPages.includes(page.page)) (page.fields || []).forEach((field) => set.add(field.id));
    });
    return set;
  }, [gradedFields, gradedPages, pageList]);

  const gradedResults = useMemo(
    () => Object.fromEntries(Object.entries(results).filter(([fieldId]) => gradedFieldSet.has(fieldId))),
    [results, gradedFieldSet]
  );

  const noteList = useMemo(() => {
    const list = [];
    currentCropList.forEach((crop) => {
      crop.fieldList.forEach((field) => {
        const result = gradedResults[field.id];
        if (result && result.reason) list.push({ id: field.id, reason: result.reason });
      });
    });
    return list.map((note, index) => ({ ...note, number: CIRCLED_NUMBER[index] || `${index + 1}` }));
  }, [currentCropList, gradedResults]);
  const noteNumberById = useMemo(
    () => Object.fromEntries(noteList.map((note) => [note.id, note.number])),
    [noteList]
  );

  const viewedRef = useRef(new Set(savedState.viewedPages));
  const stateRef = useRef({});
  stateRef.current = { ...stateRef.current, responses, results, gradedPages, gradedFields, anchor };
  if (!stateRef.current.speaking) stateRef.current.speaking = savedState.speaking;

  const save = useCallback(
    (isFinished) => {
      if (isPreview || isReview) return;
      const current = stateRef.current;
      const answer = [
        {
          type: STATE_TYPE,
          last_page: current.anchor.page,
          last_top: current.anchor.top,
          viewed_pages: Array.from(viewedRef.current).sort((a, b) => a - b),
          responses: current.responses,
          results: current.results,
          graded_pages: current.gradedPages,
          graded_fields: current.gradedFields,
          speaking: current.speaking,
        },
      ];
      dispatch(setSection({ ...section, answer }));
      dispatch(
        updateSection({
          sectionId: section.id,
          data: isFinished ? { answer, is_finished: true } : { answer },
        })
      );
    },
    [dispatch, section, isPreview, isReview]
  );

  // 조각을 넘길 때마다 저장한다. 다음에 들어오면 여기서부터 연다.
  const currentScreenKey = currentCropList.map((crop) => crop.key).join(",");
  useEffect(() => {
    if (!currentCropList.length) return;
    currentCropList.forEach((crop) => viewedRef.current.add(crop.page));
    save(false);
    setIsMemoOpen(false);
  }, [currentScreenKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveTimerRef = useRef(null);
  const handleResponseChange = (fieldId, value) => {
    setResponses((previous) => ({ ...previous, [fieldId]: value }));
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => save(false), RESPONSE_SAVE_DELAY);
  };
  useEffect(() => () => clearTimeout(saveTimerRef.current), []);
  // 칸을 떠나면(엔터 · 다른 곳 누르기) 기다리지 않고 바로 저장한다
  const commitResponses = () => {
    clearTimeout(saveTimerRef.current);
    save(false);
  };

  // 출제 때 못 받은 쪽 그림은 열 때 서버가 받아 채운다. 자를 자리(layout)도 함께 온다.
  const pageByNumber = useMemo(() => Object.fromEntries(pageList.map((page) => [page.page, page])), [pageList]);
  useEffect(() => {
    const neededList = Array.from(new Set(currentCropList.map((crop) => crop.page))).filter(
      (pageNumber) => !pageByNumber[pageNumber]?.image && !failedPages.includes(pageNumber)
    );
    neededList.forEach((pageNumber) => {
      apiClient
        .get(`/api/v1/readers-book/page?section_id=${section.id}&page=${pageNumber}`)
        .then((response) => {
          const image = response.data?.image;
          if (!image) throw new Error("no image");
          const layout = response.data?.layout;
          setPageList((previous) =>
            previous.map((one) =>
              one.page === pageNumber ? { ...one, image, ...(layout && !one.layout ? { layout } : {}) } : one
            )
          );
        })
        .catch(() => setFailedPages((previous) => [...previous, pageNumber]));
    });
  }, [currentScreenKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // 다음 화면 그림을 미리 받아 둔다
  useEffect(() => {
    (screenList[currentScreenIndex + 1] || []).forEach((index) => {
      const image = pageByNumber[cropList[index]?.page]?.image;
      if (image) new Image().src = image;
    });
  }, [currentScreenIndex, screenList, cropList, pageByNumber]);

  const ungradedFieldListOf = (crop) => crop.fieldList.filter((field) => !gradedFieldSet.has(field.id));
  const screenUngradedCount = currentCropList.reduce((sum, crop) => sum + ungradedFieldListOf(crop).length, 0);

  const gradeScreen = async () => {
    if (isGrading) return;
    const fieldListByPage = {};
    currentCropList.forEach((crop) => {
      const list = ungradedFieldListOf(crop);
      if (list.length) fieldListByPage[crop.page] = [...(fieldListByPage[crop.page] || []), ...list];
    });
    const pageNumberList = Object.keys(fieldListByPage).map(Number);
    if (!pageNumberList.length) return;
    setIsGrading(true);
    for (const pageNumber of pageNumberList) {
      const fieldList = fieldListByPage[pageNumber];
      const fieldResponses = Object.fromEntries(
        fieldList.filter((field) => field.id in responses).map((field) => [field.id, responses[field.id]])
      );
      try {
        const response = await apiClient.post("/api/v1/readers-book/grade", {
          section_id: section.id,
          page: pageNumber,
          field_ids: fieldList.map((field) => field.id),
          responses: fieldResponses,
          preview: Boolean(isPreview),
        });
        const fieldResults = response.data?.results || {};
        const settledIdList = Object.entries(fieldResults)
          .filter(([, result]) => result && !result.is_pending)
          .map(([fieldId]) => fieldId);
        stateRef.current = {
          ...stateRef.current,
          results: { ...stateRef.current.results, ...fieldResults },
          gradedFields: Array.from(new Set([...stateRef.current.gradedFields, ...settledIdList])),
        };
        setResults(stateRef.current.results);
        setGradedFields(stateRef.current.gradedFields);
        if (settledIdList.length < Object.keys(fieldResults).length) {
          alert("채점이 잠깐 밀렸어요. 조금 뒤에 다시 눌러 주세요.");
        }
      } catch (error) {
        alert("채점하지 못했어요. 다시 눌러 주세요.");
      }
    }
    setIsGrading(false);
    setIsMemoOpen(true);
  };

  const goTo = (screenIndex) => {
    const screen = screenList[Math.min(Math.max(screenIndex, 0), screenList.length - 1)];
    const crop = screen && cropList[screen[0]];
    if (!crop) return;
    setAnchor({ page: crop.page, top: crop.top });
    if (stageRef.current) stageRef.current.scrollTop = 0;
  };
  const goPrevious = () => goTo(currentScreenIndex - 1);
  const goNext = () => goTo(currentScreenIndex + 1);

  // 책 안의 '나가기'. 답은 쓰는 대로 저장되므로 이름에 저장을 넣지 않는다. 나갈 때도 한 번 더 저장하고, 다 끝낸 것으로 치지 않고 섹션 목록으로 돌아간다(끝내기는 '다 풀었어요').
  const handleExit = () => {
    clearTimeout(saveTimerRef.current);
    save(false);
    setCurrentStep(2);
  };

  const handleFinish = () => {
    if (isReview) {
      setCurrentStep(2);
      return;
    }
    const remainingCount = screenList.filter((screen) =>
      screen.some((index) => ungradedFieldListOf(cropList[index]).length > 0)
    ).length;
    if (remainingCount > 0 && !window.confirm(`아직 채점하지 않은 곳이 ${remainingCount}곳 있어요. 그래도 끝낼까요?`)) {
      return;
    }
    save(true);
    alert("학습이 완료되었습니다.");
    setCurrentStep(2);
  };

  useEffect(() => {
    const handleKey = (event) => {
      if (["INPUT", "TEXTAREA"].includes(event.target?.tagName)) return;
      if (event.key === "ArrowLeft") goPrevious();
      if (event.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  });

  const touchStartRef = useRef(null);
  const handleTouchStart = (event) => {
    const touch = event.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };
  const handleTouchEnd = (event) => {
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (!start) return;
    const touch = event.changedTouches[0];
    const distanceX = touch.clientX - start.x;
    const distanceY = touch.clientY - start.y;
    if (Math.abs(distanceX) < SWIPE_DISTANCE || Math.abs(distanceX) < Math.abs(distanceY)) return;
    if (distanceX < 0) goNext();
    else goPrevious();
  };

  if (!pageList.length) {
    return <div className="ebook__empty">문제지를 불러오지 못했어요.</div>;
  }

  const canWrite = !isReview;

  const renderCrop = (crop) => {
    const page = pageByNumber[crop.page] || {};
    const cropWidth = crop.right - crop.left;
    const cropHeight = crop.bottom - crop.top;
    // 문항 하나가 화면보다 길면(선 잇기) 조금 줄여서라도 한 화면에 넣는다. 너무 작아지면 그때는 내려 읽는다.
    const naturalWidth = tileWidthOf(cropWidth, stageSize.width, stageSize.height);
    const fitWidth = (stageSize.height * cropWidth) / (cropHeight * crop.aspect);
    const tileWidth = Math.floor(Math.max(naturalWidth * MINIMUM_FIT_SHARE, Math.min(naturalWidth, fitWidth)));
    const tileHeight = Math.round((tileWidth * cropHeight * crop.aspect) / cropWidth);
    const isCropGraded = crop.fieldList.length > 0 && crop.fieldList.every((field) => gradedFieldSet.has(field.id));
    return (
      <div className="paper-crop" key={crop.key} style={{ width: tileWidth, height: tileHeight }}>
        {page.image ? (
          <div
            className="paper-crop__page"
            style={{
              width: `${100 / cropWidth}%`,
              left: `${(-crop.left / cropWidth) * 100}%`,
              top: `${(-crop.top * crop.aspect * tileWidth) / cropWidth}px`,
              aspectRatio: `1 / ${crop.aspect}`,
            }}
          >
            <img src={page.image} alt={`${crop.page}쪽`} draggable={false} />
            {crop.fieldList.length > 0 && (
              <PageFieldLayer
                fieldList={crop.fieldList}
                responses={responses}
                results={gradedResults}
                onChange={handleResponseChange}
                onCommit={commitResponses}
                isEditable={canWrite && !isGrading}
                noteNumberById={noteNumberById}
              />
            )}
          </div>
        ) : failedPages.includes(crop.page) ? (
          <div className="ebook__page-message">
            이 쪽을 불러오지 못했어요.
            <button
              type="button"
              onClick={() => setFailedPages((previous) => previous.filter((one) => one !== crop.page))}
            >
              다시 불러오기
            </button>
          </div>
        ) : (
          <div className="ebook__page-message">불러오는 중...</div>
        )}
        {isCropGraded && <div className="paper-crop__score">{scoreTextOf(crop.fieldList, gradedResults)}</div>}
      </div>
    );
  };

  const previousButton = (
    <button type="button" className="ebook__turn" onClick={goPrevious} disabled={currentScreenIndex === 0} aria-label="앞으로">
      ‹
    </button>
  );
  const nextButton = (
    <button type="button" className="ebook__turn" onClick={goNext} disabled={isLastScreen} aria-label="다음으로">
      ›
    </button>
  );
  const pageLabel = Array.from(new Set(currentCropList.map((crop) => crop.page))).join("-");

  return (
    <div className="ebook paper-crop-view" style={{ height: BOOK_SCREEN_HEIGHT, "--ebook-accent": accent }}>
      <div className="ebook__bar">
        <div className="ebook__title">{book.title}</div>
        <div className="ebook__audio-list">
          {noteList.length > 0 && (
            <button type="button" className="ebook__memo-toggle" onClick={() => setIsMemoOpen(!isMemoOpen)}>
              첨삭 쪽지 {noteList.length}
            </button>
          )}
          <button type="button" className="ebook__exit" onClick={handleExit}>
            나가기
          </button>
        </div>
      </div>

      <div className="paper-crop-view__body">
        {!isMobile && previousButton}
        <div className="paper-crop-view__stage" ref={stageRef} onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
          <div className="paper-crop-view__screen" style={{ gap: TILE_GAP }}>
            {stageSize.width > 0 && currentCropList.map(renderCrop)}
          </div>
        </div>
        {!isMobile && nextButton}

        {isMemoOpen && noteList.length > 0 && (
          <div className="ebook__memo">
            <button type="button" className="ebook__memo-close" onClick={() => setIsMemoOpen(false)} aria-label="닫기">
              ×
            </button>
            <div className="ebook__memo-title">선생님 첨삭</div>
            {noteList.map((note) => (
              <div className="ebook__memo-line" key={note.id}>
                <span className="ebook__memo-number">{note.number}</span>
                <div>{note.reason}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="ebook__foot">
        {isMobile && previousButton}
        <div className="ebook__progress">
          <div style={{ width: `${screenList.length ? ((currentScreenIndex + 1) / screenList.length) * 100 : 0}%` }} />
        </div>
        <span className="ebook__counter">{pageLabel}쪽</span>
        {canWrite && screenUngradedCount > 0 && (
          <button type="button" className="ebook__grade" onClick={gradeScreen} disabled={isGrading}>
            {isGrading ? "채점하는 중..." : "채점하기"}
          </button>
        )}
        {isLastScreen && (
          <button type="button" className="ebook__finish" onClick={handleFinish}>
            {isReview ? "나가기" : "다 풀었어요"}
          </button>
        )}
        {isMobile && !isLastScreen && nextButton}
      </div>
    </div>
  );
};

export default PaperCropPage;
